import React, { useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import * as actions from 'actions/pathBuilder';
import * as selectors from 'selectors/pathBuilder';
import { Icon } from 'ui';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import Section, { SectionHeader } from '../Section';
import ExpirationDateModal from './ExpirationDateModal';

const unitMap = {
  1: 'day',
  2: 'week',
  3: 'month',
  4: 'year',
};

const unitMapPlural = {
  1: 'days',
  2: 'weeks',
  3: 'months',
  4: 'years',
};

export const ExpirationDateDisplay = ({
  certification: {
    expiresOn,
    expiresAfterUnit,
    expiresAfterAmount,
    renewalDueUnit,
    renews,
    repeatsAmount,
    repeatsUnit,
  },
  format = 'LL',
  reassignmentNotice = '',
}) => {
  if (
    renews &&
    expiresOn &&
    repeatsAmount &&
    repeatsUnit &&
    repeatsAmount === 1
  ) {
    return `${t(
      'certifications.certification_builder.sidebar.expiration_description_on_recurring_singular',
      {
        expiration_date: moment(expiresOn).format(format),
        expires_after_unit:
          repeatsAmount > 1 ? unitMapPlural[repeatsUnit] : unitMap[repeatsUnit],
      },
    )}${reassignmentNotice && `. ${reassignmentNotice}`}`;
  }

  if (renews && expiresOn && repeatsAmount && repeatsUnit) {
    return `${t(
      'certifications.certification_builder.sidebar.expiration_description_on_recurring',
      {
        expiration_date: moment(expiresOn).format(format),
        expires_after_amount: repeatsAmount === 1 ? '' : repeatsAmount,
        expires_after_unit:
          repeatsAmount > 1 ? unitMapPlural[repeatsUnit] : unitMap[repeatsUnit],
      },
    )}${reassignmentNotice && `. ${reassignmentNotice}`}`;
  }

  if (renews && repeatsAmount && repeatsUnit && repeatsAmount === 1) {
    return `${t(
      'certifications.certification_builder.sidebar.expiration_description_recurring_singular',
      {
        expires_after_unit: unitMap[repeatsUnit],
      },
    )}${reassignmentNotice && `. ${reassignmentNotice}`}`;
  }

  if (expiresOn) {
    return t(
      'certifications.certification_builder.sidebar.expiration_description_on',
      {
        expiration_date: moment(expiresOn).format(format),
      },
    );
  }

  if (renews && expiresAfterUnit) {
    return `${t(
      'certifications.certification_builder.sidebar.expiration_description_recurring',
      {
        expires_after_amount: expiresAfterAmount,
        expires_after_unit:
          expiresAfterAmount > 1
            ? unitMapPlural[expiresAfterUnit]
            : unitMap[expiresAfterUnit],
      },
    )}${reassignmentNotice && `. ${reassignmentNotice}`}`;
  }

  if (renews && repeatsAmount && repeatsUnit) {
    return `${t(
      'certifications.certification_builder.sidebar.expiration_description_recurring',
      {
        expires_after_amount: repeatsAmount,
        expires_after_unit:
          repeatsAmount > 1 ? unitMapPlural[repeatsUnit] : unitMap[repeatsUnit],
      },
    )}${reassignmentNotice && `. ${reassignmentNotice}`}`;
  }

  if (expiresAfterUnit && !renewalDueUnit) {
    return t(
      'certifications.certification_builder.sidebar.expiration_description_after',
      {
        expires_after_amount: expiresAfterAmount,
        expires_after_unit:
          expiresAfterAmount > 1
            ? unitMapPlural[expiresAfterUnit]
            : unitMap[expiresAfterUnit],
      },
    );
  }

  return t('certifications.expiration_types.never');
};

const ExpirationSettings = ({
  isArchived,
  certification,
  updatePath,
  pathId,
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  const updateCertification = data => {
    return request
      .putJSON(routes.PATH(pathId), {
        certification: { ...certification, ...data },
      })
      .then(response => {
        if (!response.success) {
          console.error(response);
          return response;
        }

        updatePath({ certification: response.certification });
        return response;
      })
      .catch(e => {
        console.error(e);
      });
  };

  return (
    <Section>
      <SectionHeader className="d-f jc-sb ai-fs">
        <span>{t('certifications.expiration')}</span>
        {!isArchived && (
          <button
            type="button"
            className="u-linkBtn fz-base fw-n"
            onClick={() => setModalVisible(true)}
          >
            <Icon name="edit" className="mr-4" /> {t('common.edit')}
          </button>
        )}
      </SectionHeader>

      <div className="c-brand-gray-darker">
        <ExpirationDateDisplay certification={certification} />
      </div>

      {modalVisible && (
        <ExpirationDateModal
          visible={modalVisible}
          closeModal={() => setModalVisible(false)}
          certification={certification}
          updateCertification={updateCertification}
        />
      )}
    </Section>
  );
};

const mapStateToProps = ({ pathBuilder }) => {
  return {
    pathId: pathBuilder.id,
    certification: pathBuilder.certification,
    isArchived: selectors.isArchived(pathBuilder),
  };
};

const mapDispatchToProps = {
  updatePath: actions.updatePath,
};

export default connect(mapStateToProps, mapDispatchToProps)(ExpirationSettings);
